import React, { memo, useCallback, useEffect, Suspense, useState, useMemo } from 'react'
import { Tabs, Collapse, Grid, TabsProps } from 'antd'
import { setDeliveriesStagePlanClear, setDeliveriesTimelineClear } from '../../../../store/deliveries/actions'
import { getDeliveriesTimeLineData } from '../../../../store/deliveries/action-creators'
import { DeliveryTimeline } from '../tab-timeline'
import { DeliveryStagePlan } from '../stage-plan'
import { HistoryTable } from '../../../../components/v2/history-table'
import { setHistoryClear } from '../../../../store/history/actions'
import { DeliveryFiles } from '../tab-files'
import { useAppDispatch } from '../../../../hooks/useAppDispatch/useAppDispatch'
import { DeliveriesForms } from '../deliveries-forms/deliveries-forms'
import { StyledCollapse } from '../../../../components/v2/field-group/styles'
import { TimelineTab } from '../styles'
import EntityRelations from '../../../../components/entity-relations'
import TableCallLog from '../../../clients/card/table-call-log'

interface DeliveriesTabsProps {
  className?: string
  onChange?: (key: string) => void
  onTabClick?: (key: string) => void
  id: string | number
}

export const DeliveriesTabs: React.FC<DeliveriesTabsProps> = memo((props: DeliveriesTabsProps) => {
  const { className, onChange, onTabClick, id } = props
  const dispatch = useAppDispatch()
  const [activeKey, setActiveKey] = useState<string>('data')
  const [timelineLoaded, setTimelineLoaded] = useState<boolean>(false)
  const screens = Grid.useBreakpoint()
  const isMobile = !screens?.md

  useEffect(() => {
    dispatch(setDeliveriesTimelineClear())
    dispatch(setDeliveriesStagePlanClear())
    dispatch(setHistoryClear())
    return () => {
      dispatch(setDeliveriesTimelineClear())
      dispatch(setDeliveriesStagePlanClear())
      dispatch(setHistoryClear())
    }
  }, [dispatch])

  const handleOnChange = useCallback((key) => {
    setActiveKey(key)
    if (id !== 'new') {
      if ((key === 'timeline' || key === 'files') && !timelineLoaded) {
        dispatch(getDeliveriesTimeLineData(id))
        setTimelineLoaded(true)
      }
    }
    if (onChange) {
      onChange(key)
    }
  }, [dispatch, id, timelineLoaded, onChange])

  const handleOnTabClick = useCallback((key) => {
    if (onTabClick) {
      onTabClick(key)
    }
  }, [onTabClick])

  /*
  <Tabs.TabPane tab="Лента событий" key="timeline">
    {isMobile ? (
      <>
        <StyledCollapse>
          <Collapse.Panel header={'Прогноз исполнения'} key={'stage-plan-panel'}>
            <DeliveryStagePlan id={id} hideHeader />
          </Collapse.Panel>
        </StyledCollapse>
        <StyledCollapse defaultActiveKey={['timeline-panel']}>
          <Collapse.Panel header={'Лента событий'} key={'timeline-panel'}>
            <DeliveryTimeline id={id} hideHeader />
          </Collapse.Panel>
        </StyledCollapse>
      </>
    ) : (
      <TimelineTab>
        <DeliveryStagePlan id={id} />
        <DeliveryTimeline id={id} />
      </TimelineTab>
    )}
  </Tabs.TabPane>
  */

  const tabItems = useMemo<TabsProps['items']>(() => {
    return [
      {
        key: 'data',
        label: 'Данные доставки',
        children: <DeliveriesForms id={id} />,
      },
      {
        key: 'timeline',
        label: 'Лента событий',
        destroyOnHidden: true,
        children: !isMobile ? (
          <>
            <StyledCollapse>
              <Collapse.Panel header={'Прогноз исполнения'} key={'stage-plan-panel'}>
                <DeliveryStagePlan id={id} hideHeader />
              </Collapse.Panel>
            </StyledCollapse>
            <StyledCollapse defaultActiveKey={['timeline-panel']}>
              <Collapse.Panel header={'Лента событий'} key={'timeline-panel'}>
                <DeliveryTimeline id={id} hideHeader />
              </Collapse.Panel>
            </StyledCollapse>
          </>
        ) : (
          <TimelineTab>
            <DeliveryStagePlan id={id} />
            <DeliveryTimeline id={id} />
          </TimelineTab>
        )
      },
      {
        key: 'history',
        label: 'История изменений',
        children: <Suspense fallback={''}><HistoryTable entity={'delivery'} entityId={Number(id)} /></Suspense>,
      },
      {
        key: 'files',
        label: 'Прикрепленные файлы',
        children: <DeliveryFiles id={id} />,
      },
      {
        key: 'relations',
        label: 'Связи',
        children: <EntityRelations entityType="delivery" entityId={id} />,
      },
    ]
  }, [])

  return <Tabs
    onChange={handleOnChange}
    onTabClick={handleOnTabClick}
    activeKey={activeKey}
    items={tabItems}
  />
})

export default DeliveriesTabs
